import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { IconSend } from '@components/icons/send';
import { InfoBox } from '@components/info-box';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';

import { Loading } from 'components/Loading';
import ShowMore from 'components/ShowMore';
import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';
import { IApplication } from 'models/types';

type Props = ModalProps<{
  applicants: IApplication[];
}>;

export const OnboardingFormBulkSendModal = observer((props: Props) => {
  const { closeModal, applicants } = props;
  const { t } = useStore();
  const { showError } = useToaster();
  const [templateId, setTemplateId] = useState<number>();

  const templatesQuery = useQuery({
    queryKey: ['onboardingFormTemplates'],
    queryFn: api.integrations.onboardingFormGetTemplates,
  });

  const applicationIds = applicants.map((application) => application.id);
  const sendForm = useMutation(
    () => api.integrations.onboardingFormBulkSend(templateId!, applicationIds),
    {
      onError: (error) => {
        if (api.global.isRequestError(error)) {
          showError(t(`common.errors.${error.response?.status}`));
        }
      },
    }
  );

  if (!templatesQuery.isSuccess) {
    return <Loading />;
  }

  if (sendForm.isSuccess) {
    return (
      <Modal>
        <Modal.Header text={t('integrations.onboardingform.bulk.modal.sent.header')} />
        <Text text={t('integrations.onboardingform.bulk.modal.sent.text')} />
        <Modal.Actions>
          <Button
            variant="secondary"
            onClick={closeModal}
            text={t('modals.confirm.button.close')}
          />
        </Modal.Actions>
      </Modal>
    );
  }

  return (
    <Modal>
      <Modal.Header text={t('integrations.onboardingform.bulk.modal.header')} />
      {applicants.length === 0 && (
        <>
          <Text text={t('integrations.onboardingform.bulk.modal.noapplicants')} />
          <Modal.Actions>
            <Button
              variant="secondary"
              onClick={closeModal}
              text={t('modals.confirm.button.close')}
            />
          </Modal.Actions>
        </>
      )}
      {applicants.length > 0 && (
        <Stack space={4}>
          <Text text={t('integrations.onboardingform.bulk.modal.send.header')} />
          <InfoBox text={t('integrations.onboardingform.bulk.modal.info')} variant="blue" />
          <ShowMore
            max={applicants.length}
            count={applicants.length > 15 ? 10 : applicants.length}
            inc={1000}
            render={({ count }: { count: number }) => (
              <div>
                {applicants.slice(0, count).map((a, i) => (
                  <Text font="bold16" key={i} text={a.name} />
                ))}
              </div>
            )}
          />
          <Select
            options={templatesQuery.data.map((template) => {
              return { value: template.id.toString(), label: template.title };
            })}
            onChange={(value) => setTemplateId(Number(value))}
            label={t('integrations.onboardingform.bulk.modal.templates.label')}
          />
          <Modal.Actions>
            <Button
              variant="secondary"
              onClick={closeModal}
              isDisabled={sendForm.isLoading}
              text={t('modals.confirm.button.cancel')}
            />
            <Button
              variant="primary"
              onClick={() => sendForm.mutate()}
              isDisabled={sendForm.isLoading || !templateId}
              text={t('integrations.onboardingform.bulk.modal.submit')}
              iconLeft={<IconSend />}
            />
          </Modal.Actions>
        </Stack>
      )}
    </Modal>
  );
});
